import { Navigate } from "react-router-dom";
import { useEffect, useState } from "react";

// Protected route types
import { IRouteProtectorProps } from "./RouteProtectorTypes";
import { IUser } from "../shared/commonTypes/commonTypes";

// Multi-lingual support
import { t } from "i18next";

// Hooks
import { useUser } from "../../hooks/useUserContext/useUserContext";
import { useLoader } from "../../hooks/useLoaderContext/useLoaderContext";
import { useToaster } from "../../hooks/useToasterContext/useToasterContext";

// API Manager
import { getApi } from "../../services/apiManager/ApiManager";

// Components
import GlobalLoader from "../layout/GlobalLoader";

// Protected route component
const RouteProtector: React.FC<IRouteProtectorProps> = ({
  allowedRoles,
  children,
}) => {
  const { user, setUser } = useUser();
  const { setLoading, isLoading } = useLoader();
  const { setShowToaster } = useToaster();
  const [isUserFetched, setIsUserFetched] = useState(false);

  useEffect(() => {
    if (!isUserFetched) {
      // Simulate an API call or user fetch logic
      const fetchUser = async () => {
        try {
          setLoading(true);
          const response = await getApi("/v1/user/current");
          if (response.status) {
            const user: IUser = {
              name: response.data.name,
              role: response.data.roles[0].name,
            };
            setUser(user);
          }
        } catch (error) {
          setShowToaster(
            true,
            t("commonMessages.internalServerError"),
            "error",
          );
        } finally {
          setLoading(false);
          setIsUserFetched(true); // Indicate that the user fetching logic is complete
        }
      };

      fetchUser();
    }
  }, []);

  if (isLoading || !isUserFetched) {
    return <GlobalLoader />;
  } else if (!user) {
    return <Navigate to="/authenticate" replace />;
  } else if (!allowedRoles.includes(user.role)) {
    return <Navigate to="/accessdenied" replace />;
  }

  return <>{children}</>;
};

export default RouteProtector;
